import { isOnSameDate, getDateWithDaylineSet } from '../../libs/dateTimeUtilities';
import NoRoster from './NoRoster';
import RosterSummarizedBooking from './RosterSummarizedBooking';

export default class DayRosters {
  constructor(dateFormatSetting, timeFormatSetting, dayStart, dayEnd) {
    this.rosters = [];
    this.dayStart = dayStart;
    this.dayEnd = dayEnd;
    this.dateFormatSetting = dateFormatSetting;
    this.timeFormatSetting = timeFormatSetting;
  }

  allHaveActuals() {
    return this.rosters.every((rosterItem) => rosterItem.allHaveActuals());
  }

  getStartOfWork() {
    const firstNonUnavailability = this.rosters.find(
      (roster) => roster.type !== 'unavailability'
    );
    return firstNonUnavailability ? firstNonUnavailability.start : null;
  }

  getEndOfWork() {
    const lastNonUnavailability = this.rosters
      .slice()
      .reverse()
      .find((roster) => roster.type !== 'unavailability');
    return lastNonUnavailability ? lastNonUnavailability.end : null;
  }

  getRosters() {
    return this.rosters;
  }

  hasRosters() {
    return this.rosters.length > 0;
  }

  addNoRosters() {
    // If we have multiple shifts on the same date, then we should add a 'no roster' element in between the shifts
    let currentShift = null;
    const noRosters = [];
    this.rosters.forEach((rosterItem) => {
      if (rosterItem.type === 'shift') {
        if (currentShift === null) {
          // Keep track of the first shift that we encounter
          currentShift = rosterItem;
        } else {
          // We encountered a second shift; check if there is a gap in between and if so, then add a 'no roster'
          if (currentShift.end.isBefore(rosterItem.start))
            noRosters.push(new NoRoster(currentShift.end, rosterItem.start));
          currentShift = rosterItem;
        }
      }
    });

    // If we have 'no rosters', then add them and re-sort
    if (noRosters.length !== 0) {
      noRosters.forEach((noRoster) => this.rosters.push(noRoster));
      this.sort();
    }
  }

  isOnNextDayBeforeDayline(rosterItemDateTime, day, dayline) {
    const dayAfter = day.clone().add(1, 'd');
    const dayAfterStart = getDateWithDaylineSet(day.clone().add(1, 'd'), dayline);
    return (
      isOnSameDate(rosterItemDateTime, dayAfter) &&
      rosterItemDateTime.isBefore(dayAfterStart)
    );
  }

  isBeforeDayline(rosterItemDateTime, day, dayline) {
    const dayStart = getDateWithDaylineSet(day, dayline);
    return isOnSameDate(rosterItemDateTime, day) && rosterItemDateTime.isBefore(dayStart);
  }

  rosterItemStartsOrEndsThisDay(rosterItem, day) {
    return isOnSameDate(rosterItem.start, day) || isOnSameDate(rosterItem.end, day);
  }

  filterOutBookingsOnOtherDay(day, dayline) {
    this.rosters = this.rosters.filter((rosterItem) => {
      let showRosterItem = true;
      /* Roster item starts/ends not this dag */
      if (!this.rosterItemStartsOrEndsThisDay(rosterItem, day)) {
        showRosterItem = false;
        /* Roster item starts/ends before dayline of next day,
         * which means it has to be displayed on this day and may not be filtered out  */
        if (
          this.isOnNextDayBeforeDayline(rosterItem.start, day, dayline) ||
          this.isOnNextDayBeforeDayline(rosterItem.end, day, dayline)
        ) {
          showRosterItem = true;
        }
        /* If rosteritem ends before current dayline don't show it again, this causes double roster items  */
      } else if (
        isOnSameDate(rosterItem.end, day) &&
        this.isBeforeDayline(rosterItem.end, day, dayline)
      ) {
        showRosterItem = false;
      }

      return showRosterItem;
    });
    return this;
  }

  sort() {
    this.rosters.forEach((roster) => roster.sort());
    this.rosters.sort((r1, r2) => r1.start - r2.start);
  }

  addShift(newShift) {
    // Adding a shift means either merging it with a shift that we already encountered, or adding a new shift
    // We merge if the shift really overlaps, or is right before/after an already present shift
    const overlappingShift = this.rosters.find((roster) => roster.canBeMerged(newShift));
    if (overlappingShift) overlappingShift.mergeWith(newShift);
    else this.rosters.push(newShift);
  }

  addUnavailability(newUnavailability) {
    const overlappingUnavailability = this.rosters.find((roster) =>
      roster.canBeMerged(newUnavailability)
    );
    if (overlappingUnavailability) overlappingShift.mergeWith(newUnavailability);
    else this.rosters.push(newUnavailability);
  }

  addBreak(newBreak) {
    // This method works in two steps

    // First, we see if there is an overlapping/adjacent break that we can merge this break with
    let breakToProcess = newBreak;
    const mergableBreak = this.rosters.find((roster) => roster.canBeMerged(newBreak));
    if (mergableBreak) {
      mergableBreak.mergeWith(newBreak);
      breakToProcess = mergableBreak;
    } else {
      this.rosters.push(newBreak);
    }

    // Second, we look for overlapping shifts (and keep doing so as long as we can find them)
    let overlappingShift = this.rosters.find((roster) =>
      roster.overlapsWith(breakToProcess)
    );
    while (overlappingShift) {
      // We found an overlapping shift, so we add the break to this shift
      // This will result in that the times of the shift are updated, and that the shift is potentially split in two
      // If this is the case, we get the split shift back and need to add it too
      const splitShift = overlappingShift.addBreak(breakToProcess);
      if (splitShift) this.rosters.push(splitShift);
      // It can happen that adding a break results in a zero-duration shift: we need to remove such shifts in order to prevent infinite loops
      this.rosters = this.rosters.filter((roster) => !roster.shouldBeRemoved());
      overlappingShift = this.rosters.find((roster) =>
        roster.overlapsWith(breakToProcess)
      );
    }
  }

  addBooking(newBooking) {
    // Adding a booking means that we need to add it to a shift or to a booking summary
    const overlappingShiftOrBookingSummary = this.rosters.find((roster) =>
      roster.overlapsWith(newBooking)
    );
    // If there is such an overlapping shift or summary, we add the booking to this shift or summary
    // Else, we need to add a new summarized booking for the new booking
    if (overlappingShiftOrBookingSummary) {
      // Adding the booking to a shift or summarized booking can result in that the original booking is split up
      // For example, consider a booking that partially overlaps with a shift: in such a case we need to keep processing the remaining pieces of the booking that we are adding (the part before and/or after the shift)
      const remaindersOfBooking = overlappingShiftOrBookingSummary.addBooking(
        newBooking,
        (summarizedBooking) => this.rosters.push(summarizedBooking)
      );
      // So recursively process the remainder of the booking
      remaindersOfBooking.forEach((remainderOfBooking) =>
        this.addBooking(remainderOfBooking)
      );
    } else {
      this.rosters.push(
        new RosterSummarizedBooking([newBooking], newBooking.start, newBooking.end)
      );
    }
  }
}
